/**
 * Formatting for values that arrive from the API as **strings**.
 *
 * Every Decimal on the wire — prices, litres, tank volumes — is serialized as a string, and it
 * stays a string here. The client formats; it never does arithmetic on money. Two rules make
 * the difference between right and subtly wrong:
 *
 * - **Parse only the dot form.** `parseFloat` on `"2190.00"` is correct; anything locale-aware
 *   reads `"300.00"` as thirty thousand where the dot is a thousands separator. iOS pins this
 *   with `en_US_POSIX` for the same reason.
 * - **Reject before parsing.** `parseFloat("55литров")` is 55 and `parseFloat("1e2")` is 100.
 *   A strict pattern runs first, so a malformed value shows as itself rather than as a number
 *   someone did not type.
 *
 * A value we cannot parse is shown as-is with its unit rather than hidden: «N/A ₽» tells the
 * truth, an empty cell does not.
 */

/**
 * U+00A0, written as an escape on purpose: an invisible literal in the source is
 * indistinguishable from a plain space, and the difference only shows up as a failing
 * comparison somewhere far away.
 */
const NBSP = '\u00A0';

const DECIMAL_STRING = /^-?\d+(\.\d+)?$/;

function parseDecimal(raw: string): number | null {
  const trimmed = raw.trim();
  if (!DECIMAL_STRING.test(trimmed)) return null;
  const value = Number.parseFloat(trimmed);
  return Number.isFinite(value) ? value : null;
}

function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

/**
 * «2 190 ₽» for whole roubles, «2 190,50 ₽» when the kopecks matter.
 *
 * Kopecks are dropped only when they are actually zero — never rounded away, because the number
 * next to a rouble sign is what a person checks against their bank app.
 */
export function formatMoney(raw: string | null | undefined): string {
  if (raw === null || raw === undefined || raw.trim().length === 0) return '';
  const value = parseDecimal(raw);
  if (value === null) return `${raw.trim()}${NBSP}₽`;

  const negative = value < 0;
  const absolute = Math.abs(value);
  const whole = Math.trunc(absolute);
  const kopecks = Math.round((absolute - whole) * 100);

  const body =
    kopecks === 0
      ? groupThousands(String(whole))
      : `${groupThousands(String(whole))},${String(kopecks).padStart(2, '0')}`;

  return `${negative ? '−' : ''}${body}${NBSP}₽`;
}

/**
 * «27,4 л» — up to two decimals, trailing zeros dropped, no thousands grouping.
 *
 * The grouping matters: a rider reading «60 000 л» where the value was «60.000» was a real
 * report (audit 2026-08-17), and litres never reach four digits anyway.
 */
export function formatLiters(raw: string | null | undefined): string {
  const number = formatDecimalPlain(raw);
  return number === null ? '' : `${number}${NBSP}л`;
}

/**
 * The bare number, comma-separated, trailing zeros dropped: `"55.000"` → `"55"`,
 * `"55.500"` → `"55,5"`.
 *
 * This is what a tank volume looks like in a form field. The server stores `numeric(10,3)` and
 * returns `"55.000"`, which in an input box reads as a typo rather than as a volume.
 */
export function formatDecimalPlain(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined || raw.trim().length === 0) return null;
  const value = parseDecimal(raw);
  if (value === null) return raw.trim();

  const rounded = value.toFixed(2).replace(/\.?0+$/, '');
  return rounded.replace('.', ',');
}

/** True when a Decimal string means «nothing» — `"0"`, `"0.00"` and unparseable all count. */
export function isZeroAmount(raw: string | null | undefined): boolean {
  if (raw === null || raw === undefined) return true;
  const value = parseDecimal(raw);
  return value === null || value === 0;
}

const TIME_FORMAT = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' });
const DAY_MONTH_FORMAT = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' });
const DAY_MONTH_YEAR_FORMAT = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * «сегодня, 15:08» · «вчера, 14:52» · «12 июля, 15:08» · «12 июля 2025 г., 15:08».
 *
 * Today and yesterday are decided in the **device's** timezone, not UTC — the boundary a person
 * means by «вчера» is their midnight. An unparseable timestamp yields an empty string rather
 * than throwing: one bad row must not take the list down with it.
 */
export function formatNotificationTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const time = TIME_FORMAT.format(date);
  const daysApart = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);

  if (daysApart === 0) return `сегодня, ${time}`;
  if (daysApart === 1) return `вчера, ${time}`;

  const sameYear = date.getFullYear() === now.getFullYear();
  // «12 июля 2025, 15:08» — the trailing «г.» that ru-RU adds is dropped, the same way
  // `formatMemberSince` drops it. Two date lines in one app should not disagree about it.
  const day = sameYear
    ? DAY_MONTH_FORMAT.format(date)
    : DAY_MONTH_YEAR_FORMAT.format(date).replace(/\s*г\.$/, '');
  return `${day}, ${time}`;
}

/**
 * «С нами с июля 2026» — the profile card's membership line.
 *
 * The month has to be in the genitive («июля», not «июль»), and `Intl` only produces that inside
 * a full date. So the date is formatted with a day and the day is then stripped, which is
 * exactly the trick iOS uses to avoid the same nominative-form mistake.
 */
export function formatMemberSince(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const full = DAY_MONTH_YEAR_FORMAT.format(date); // «21 августа 2026 г.»
  const withoutDay = full.replace(/^\d+\s+/, '');
  return withoutDay.replace(/\s*г\.$/, '');
}

/**
 * «25.08.2026, 15:34» — абсолютная отметка времени для журнала согласий.
 *
 * Не `formatNotificationTime`: там «сегодня» и «вчера», и это правильно для ленты уведомлений, но
 * не для записи, которую предъявляют в споре. «Сегодня, 15:34» через полгода не значит ничего;
 * дата с годом значит. Год выводится всегда, даже для этого года — по той же причине.
 *
 * Время местное, как и везде в приложении: человек должен узнать момент, в который он нажал
 * кнопку. Точная UTC-отметка с микросекундами живёт в базе и достаётся оттуда, если дело дойдёт
 * до разбирательства.
 */
export function formatLegalTimestamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${formatShortDate(iso)}, ${TIME_FORMAT.format(date)}`;
}

/** «12.07.2025» — the short form used on the account-deletion receipt. */
export function formatShortDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${date.getFullYear()}`;
}
